import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

import { DataViewPromoteDialogComponent } from './data-view-promote-dialog/data-view-promote-dialog.component';
import { ConfirmDialog, DialogService } from '../dialogs';
import { ApiParams, CoreApiService } from '../core-api.service';
import { LoggingService } from '../logging.service';
import { NotificationService } from '../notification.service';
import { ProfileService } from '../profile/profile.service';
import { downloadFile } from '../shared/browser.utils';
import { DataView, DataViewExecution, DataViewPayload } from '../shared/models/data-view.model';
import { DownloadTicket } from '../shared/models/download.model';
import { Paginated } from '../shared/models/paginated.model';
import { TranslationBundle, TranslationService } from '../translation.service';
import { Router } from '@angular/router';

export enum DataViewAction {
  Edit = 'edit',
  Copy = 'copy',
  CopyToProject = 'copyToProject',
  Delete = 'delete',
  Promote = 'promote',
  View = 'view',
}

export type DataViewContext = 'container' | 'site';

@Injectable()
export class DataViewsService implements OnDestroy {
  hasProjectDataViews$ = new BehaviorSubject<boolean>(false)
  hasSharedDataViews$ = new BehaviorSubject<boolean>(false)
  hasDataViewExecutions$ = new BehaviorSubject<boolean>(false)

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    exportData: Subscription.EMPTY,
    message: Subscription.EMPTY,
  }

  constructor(
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private translate: TranslationService,
    private router: Router,
  ) {
    this.subscriptions.message = this.translate.getBundle('dataViews').subscribe((messages) => {
      this.messages = messages;
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isSiteContext(): boolean {
    return this.router.url.startsWith('/admin/dataviews');
  }

  isSharedDataView(dataView?: Partial<DataView>): boolean {
    return dataView?.parent === 'site';
  }

  fetchDataViews(containerId: string, params: ApiParams): Observable<Paginated<DataView[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap((exhaustive) => {
        params.exhaustive = exhaustive;
        return this.coreApi.views.query(containerId, params).pipe(
          tap((resp) => {
            if (params.filter === 'parent!=site') {
              this.hasProjectDataViews$.next(resp.total > 0);
            } else if (resp.total > 0) {
              this.hasSharedDataViews$.next(resp.total > 0);
            }
          }),
        );
      }),
    );
  }

  fetchDataViewExecutions(containerId: string, params: ApiParams): Observable<Paginated<DataViewExecution[]>> {
    return this.profileService.exhaustiveMode$.pipe(
      switchMap((exhaustive) => {
        params.exhaustive = exhaustive;
        return this.coreApi.dataViewExecutions.query(params).pipe(
          tap((resp) => {
            this.hasDataViewExecutions$.next(resp.total > 0);
          }),
        );
      }),
    );
  }

  queueDataViewExecution(dataViewId: string, containerId: string): Observable<DataViewExecution> {
    return this.coreApi.views.queue.saved(dataViewId, containerId).pipe(
      tap(() => {
        this.notifications.success('views.queue.success');
      }),
      catchError((error) => {
        this.notifications.error('views.queue.error');
        this.logger.error(error);
        throw error;
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveDataView(id: string, viewData: DataViewPayload, action: 'update' | 'create'): Observable<any> {
    return (
      action === 'update' && this.isSiteContext
        ? this.dialog.open(ConfirmDialog, { data: { content: this.messages.get('content.siteTemplateSaveConfirmation') } }).afterClosed()
        : of(true)
    ).pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.views[action](id, viewData)),
      tap(() => {
        this.notifications.success(this.isSharedDataView(viewData) ? `views.templates.${action}.success` : `views.${action}.success`);
      }),
      catchError((error) => {
        this.notifications.error(this.isSharedDataView(viewData) ? `views.templates.${action}.error` : `views.${action}.error`);
        throw error;
      }),
    );
  }

  exportViewData(containerId: string, dataViewId: string, dataViewLabel: string, format = 'csv'): Observable<DownloadTicket> {
    return this.coreApi.views.data.ticket(dataViewId, { containerId, format }).pipe(
      tap((ticket) => {
        const filename = `${dataViewLabel}.${format.includes('json') ? 'json' : format}`;
        downloadFile(
          dataViewLabel,
          `/api/views/${dataViewId}/data?ticket=${ticket.ticket}&filename=${filename}&containerId=${containerId}&format=${format}`
        );
      }),
      catchError(error => {
        this.logger.error(error);
        this.notifications.error('views.export.error');
        throw error;
      }),
    );
  }

  exportExecutionData(dataViewExecutionId: string, dataViewExecutionLabel: string, format = 'csv'): Observable<DownloadTicket> {
    return this.coreApi.dataViewExecutions.data.ticket(dataViewExecutionId, format).pipe(
      tap((ticket) => {
        downloadFile(
          dataViewExecutionLabel,
          `/api/data_view_executions/${dataViewExecutionId}/data?ticket=${ticket.ticket}&file_name=${dataViewExecutionLabel}&file_format=${format.includes('json') ? 'json' : format}`
        );
      }),
      catchError(error => {
        this.logger.error(error);
        this.notifications.error('views.export.error');
        throw error;
      }),
    );
  }

  deleteDataViewExecution(execution: DataViewExecution): Observable<void> {
    return this.dialog.open(ConfirmDialog, {
      data: {
        content: this.translate.interpolate(this.messages.get('content.dataViewExecutionDeleteConfirmation'), execution.data_view_label),
      },
    }).afterClosed().pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.dataViewExecutions.delete(execution._id)),
      tap(() => {
        this.notifications.success('views.delete.success');
      }),
      catchError(error => {
        this.notifications.error('views.delete.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  deleteDataView(view: DataView): Observable<void> {
    return this.dialog.open(ConfirmDialog, {
      data: {
        html: this.isSiteContext
          ? this.translate.interpolate(this.messages.get('content.siteTemplateDeleteConfirmation'), view.label)
          : this.translate.interpolate(this.messages.get('content.dataViewDeleteConfirmation'), view.label),
      },
    }).afterClosed().pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.views.delete(view._id)),
      tap(() => {
        this.notifications.success(this.isSiteContext ? 'views.templates.delete.success' : 'views.delete.success');
      }),
      catchError(error => {
        this.notifications.error(this.isSiteContext ? 'views.templates.delete.error' : 'views.delete.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  promoteDataView(data: DataView): void {
    this.dialog.open(DataViewPromoteDialogComponent, { data });
  }

  getActionTooltip(label: string, option: DataViewAction | DataViewAction[keyof DataViewAction]): string {
    switch (option) {
      case DataViewAction.Edit: {
        return this.translate.interpolate(this.messages.get('tooltips.modify'), label);
      }
      case DataViewAction.Copy: {
        return this.translate.interpolate(
          this.messages.get('tooltips.copy'),
          label,
          this.messages.get(`labels.${this.isSiteContext ? 'sharedDataViews' : 'currentProject'}`).toLowerCase()
        );
      }
      case DataViewAction.Delete: {
        return this.translate.interpolate(
          this.messages.get('tooltips.delete'),
          label,
          this.messages.get(`labels.${this.isSiteContext ? 'sharedDataViews' : 'currentProject'}`).toLowerCase()
        );
      }
      case DataViewAction.Promote: {
        return this.translate.interpolate(this.messages.get('tooltips.promote'), label);
      }
      default: {
        return '';
      }
    }
  }
}
